import {
  AtomicFilter,
  CollatorType,
  FilterOperator,
  JsonObjectSearchFilterVisitor,
  MARKETPLACE_PROJECTS_CONSUMABLE,
} from '@agilelab/plugin-wb-search-common';
import { useApi } from '@backstage/core-plugin-api';
import { JsonObject } from '@backstage/types';
import useAsync from 'react-use/lib/useAsync';
import { witboostSearchApiRef } from '../api';
import { resolveAvailableValues } from '../filterResolvers';
import {
  FacetedValues,
  FilterResolversOverride,
  SearchFiltersContext,
} from '../types';
import { useSearchFilterConfig } from './useSearchFilterConfig';
import { ConsumableMode } from '@agilelab/plugin-wb-marketplace-common';
import { useFacetedValues } from './useFacetedValues';
import { useMemo } from 'react';

const DefaultFiltersOverride = {};

export const useSearchFilters = (
  type: CollatorType,
  filterResolversOverride: FilterResolversOverride = DefaultFiltersOverride,
  currentFilters?: JsonObject,
  queryTerm?: string,
  taxonomyRef?: string,
): SearchFiltersContext => {
  const { filtersConfig, filtersConfigReady } = useSearchFilterConfig(
    type,
    taxonomyRef,
  );
  const searchApi = useApi(witboostSearchApiRef);

  const consumableFilterJsonObject = new AtomicFilter(
    MARKETPLACE_PROJECTS_CONSUMABLE.field,
    FilterOperator.IN,
    [ConsumableMode.Consumable, ConsumableMode.HasConsumableChild],
  ).accept(new JsonObjectSearchFilterVisitor());

  // TODO: put preFilter as optional input param
  const preFilters: JsonObject =
    type === CollatorType.MARKETPLACE_PROJECTS
      ? consumableFilterJsonObject
      : undefined;

  // We exclude values related to not consumable entities
  const availableValues = useAsync(async () => {
    if (!filtersConfigReady) {
      return new Promise<never>(() => {});
    }
    return resolveAvailableValues(
      type,
      filtersConfig,
      searchApi,
      filterResolversOverride,
      preFilters,
    );
  }, [type, filtersConfig, filterResolversOverride, filtersConfigReady]);

  const facetedValues = useAsync(async () => {
    if (!filtersConfigReady) {
      return new Promise<never>(() => {});
    }

    const raw = await searchApi.getAvailableFilterValues(type, {
      currentFilters,
      queryTerm,
    });

    const res: FacetedValues = {};

    // transform into an object where the value of each filter is mapped to its count for convenience
    Object.entries(raw).forEach(([label, counts]) => {
      res[label] = counts.reduce<Record<string, number>>(
        (acc, { value, count }) => {
          acc[value] = count ? parseInt(count, 10) : 0;
          return acc;
        },
        {},
      );
    });

    return res;
  }, [type, currentFilters, queryTerm, filtersConfigReady]);

  const enrichedValues = useFacetedValues(
    availableValues.value,
    facetedValues.value,
    filtersConfig,
  );

  const result = useMemo(
    () => ({
      config: filtersConfig,
      availableValues,
      facetedValues,
      enrichedValues,
    }),
    [filtersConfig, availableValues, facetedValues, enrichedValues],
  );

  return result;
};
